import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, TextField, Typography } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { FineDetailsPublicResponse, getFinesDetails } from "api"
import { useLocale, useTranslations } from "next-intl"
import { Dispatch, LegacyRef, SetStateAction, useEffect, useRef } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, useForm } from "react-hook-form"
import { CustomHelperText } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Link } from "utils"
import * as yup from "yup"
import { ErrorType } from "../Payment"
import InfoPopup from "./InfoPopup"

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY_EGGED
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledForm = styled.form`
  display: flex;
  width: 100%;

  gap: 24px;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 24px;
    width: 880px;
    gap: 32px;
    border-radius: 12px;
    border: 1px solid ${sharedColors.borderGray};
    background: var(--White, #fff);
    box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);
  }
`

const TextfieldsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 44px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 24px;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    justify-content: space-between;
    align-items: flex-end;
    flex-direction: row;
  }
`
const StyledButton = styled(Button)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 260px;
  }
`

const StyledLink = styled(Link)`
  color: ${sharedColors.black};
  text-decoration: underline;
`

export default function Form({
  setIsLoading,
  toSecondStage,
  setData,
  setError,
}: {
  setIsLoading: Dispatch<SetStateAction<boolean>>
  toSecondStage: () => void
  setData: Dispatch<SetStateAction<FineDetailsPublicResponse | undefined>>
  setError: Dispatch<SetStateAction<ErrorType | undefined>>
}) {
  const locale = useLocale()
  const tInner = useTranslations("Forms")
  const reCaptchaRef = useRef<ReCAPTCHA>()
  const fineIdRef = useRef<HTMLInputElement>()
  const fineNumeratorRef = useRef<HTMLInputElement>()

  const schema = yup.object({
    fineId: yup
      .string()
      .matches(/^\d+$/, `${tInner("reportNumber")}: ${tInner("invalidNumber")}`)
      .max(13, `${tInner("reportNumber")}: ${tInner("maxFineId")}`)
      .required(`${tInner("reportNumber")}: ${tInner("invalidNumber")}`),
    fineNumerator: yup
      .string()
      .matches(/^\d+$/, `${tInner("formNo")}: ${tInner("invalidNumber")}`)
      .max(14, `${tInner("formNo")}: ${tInner("maxFineNumerator")}`)
      .required(`${tInner("formNo")}: ${tInner("invalidNumber")}`),
  })

  type FormData = yup.InferType<typeof schema>
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
  } = useForm<FormData>({
    defaultValues: {
      fineId: "",
      fineNumerator: "",
    },
    resolver: yupResolver(schema),
  })

  // Focus management for accessibility
  useEffect(() => {
    if (isSubmitted && Object.keys(errors).length > 0) {
      if (errors.fineId && fineIdRef.current) {
        fineIdRef.current.focus()
      } else if (errors.fineNumerator && fineNumeratorRef.current) {
        fineNumeratorRef.current.focus()
      }
    }
  }, [errors, isSubmitted])

  const { mutate } = useMutation({
    mutationFn: async ({
      fineId,
      fineNumerator,
      recaptcha,
    }: {
      fineId: string
      fineNumerator: string
      recaptcha: string
    }) => {
      try {
        const res = await getFinesDetails(
          Number(fineId),
          Number(fineNumerator),
          recaptcha,
        )
        return res.data.body
      } catch (err) {
        console.log(err)
      }
    },
    onMutate: () => {
      setIsLoading(true)
    },
    onSettled: () => {
      setIsLoading(false)
    },
    onSuccess: (data) => {
      if (data) {
        const canPay = data.paymentStatus === 1 && data.actionType === 1
        const paymentStatus = data.paymentStatus
        const actionType = data.actionType
        if (canPay) {
          setData(data)
          toSecondStage()
        } else if (paymentStatus === 2) {
          setError("alreadyPaid")
        } else if (actionType === 2) {
          setError("notPayable")
        } else if (
          paymentStatus === 5 ||
          paymentStatus === 6 ||
          paymentStatus === 7
        ) {
          setData(data)

          setError("inProcess")
        } else if (paymentStatus === 3) {
          setError("cancelled")
        }
      } else {
        setError("invalidForm")
      }
    },
    onError: (error) => {
      console.log(error)
      setError("invalidForm")
    },
  })

  const onSubmit = async (data: FormData) => {
    try {
      reCaptchaRef?.current?.reset()
      const recaptchaResponse =
        (await reCaptchaRef?.current?.executeAsync()) as string
      mutate({
        fineId: data.fineId,
        fineNumerator: data.fineNumerator,
        recaptcha: recaptchaResponse,
      })
    } catch (error) {
      console.log(error)
    }
  }
  return (
    <>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="body2">
          {tInner("mandatoryDetails")}
          <span style={{ color: "#E00700" }}>*</span>
        </Typography>
        <TextfieldsBox>
          <Controller
            name="fineId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={fineIdRef}
                label={
                  <Typography variant="body2">
                    {tInner("reportNumber")}{" "}
                    <span style={{ color: "#E00700" }}>*</span>
                  </Typography>
                }
                type="text"
                variant="filled"
                error={!!errors.fineId}
                aria-required="true"
                aria-describedby={errors.fineId ? "fineId-error" : undefined}
                helperText={
                  <CustomHelperText
                    render={isSubmitted}
                    error={!!errors.fineId}
                    text={errors.fineId?.message}
                  />
                }
                FormHelperTextProps={{
                  id: "fineId-error",
                }}
                slotProps={{
                  input: {
                    endAdornment: <InfoPopup type="fine" />,
                    autoComplete: "off",
                  },
                }}
                fullWidth
              />
            )}
          />
          <Controller
            name="fineNumerator"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={fineNumeratorRef}
                label={
                  <Typography variant="body2">
                    {tInner("formNo")}{" "}
                    <span style={{ color: "#E00700" }}>*</span>
                  </Typography>
                }
                type="text"
                variant="filled"
                error={!!errors.fineNumerator}
                aria-required="true"
                aria-describedby={
                  errors.fineNumerator ? "fineNumerator-error" : undefined
                }
                helperText={
                  <CustomHelperText
                    render={isSubmitted}
                    error={!!errors.fineNumerator}
                    text={errors.fineNumerator?.message}
                  />
                }
                FormHelperTextProps={{
                  id: "fineNumerator-error",
                }}
                slotProps={{
                  input: {
                    endAdornment: <InfoPopup type="form" />,
                    autoComplete: "off",
                  },
                }}
                fullWidth
              />
            )}
          />
        </TextfieldsBox>
        <ButtonsContainer>
          <StyledButton variant="primary" type="submit">
            {tInner("next")}
          </StyledButton>
        </ButtonsContainer>
      </StyledForm>
      {locale === "he" ? (
        <Typography variant="body1">
          כל המידע בנוגע לנסיעה ללא הסדר תשלום נמצא{" "}
          <StyledLink href="/information-for-passengers/payment-of-an-increased-rate-report/fine-details">
            כאן
          </StyledLink>
        </Typography>
      ) : (
        <Typography variant="body1">
          All the information regarding traveling without a valid payment
          arrangement can be found{" "}
          <StyledLink href="/information-for-passengers/payment-of-an-increased-rate-report/fine-details">
            here
          </StyledLink>
        </Typography>
      )}
      {RECAPTCHA_KEY && (
        <ReCAPTCHA
          ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
          sitekey={RECAPTCHA_KEY}
          hl="iw"
          tabIndex={-1}
          size="invisible"
        />
      )}
    </>
  )
}
